import { Icon } from "@iconify/react";
import { Colors } from "../../../utils/colors";
import { useCallback, useMemo, useState, useRef } from "react";
import PopupModalContainer, {
  PopupModalContainerWidth,
} from "../../Containers/PopupModalContainer";
import { useDispatch } from "react-redux";
import { RWebShare } from "react-web-share";
import { useTheme } from "../../../hooks/useTheme";
import AlertButtonWrapper from "../../Containers/AlertButtonWrapper";
import { NotificationType } from "@markit/common.types";
import { showNotificationBanner } from "../../../utils/notificationUtils";
import RectangleButton from "../../Buttons/RectangleButton";
import QRCode from "react-qr-code";
import { onShareLink } from "../../../utils/shareLink";
import { isDesktop } from "react-device-detect";
import { MARKITAI } from "@markit/common.utils";

export type ShareMembershipLinkModalProps = {
  username: string;
  setShareLinkModal: (shareLinkModal: boolean) => void;
};

// Share modal for the membership link (can make this generic if we want to have this type of modal for other share links)
const ShareMembershipLinkModal = (props: ShareMembershipLinkModalProps) => {
  const { username, setShareLinkModal } = props;
  const dispatch = useDispatch();
  const { theme } = useTheme();
  const qrCodeRef = useRef<HTMLDivElement>(null);

  const [alertText, setAlertText] = useState("");
  const [qrCodeModal, setQrCodeModal] = useState(false);

  const styles = {
    smallText: { fontSize: 11, paddingTop: 7 },
  };
  const shareURL = useMemo(
    () => `${MARKITAI}/u/${username}/memberships`,
    [username]
  );

  const onCopyClick = useCallback(() => {
    const el = document.createElement("input");
    el.value = shareURL;
    document.body.appendChild(el);
    el.select();
    document.execCommand("copy");
    document.body.removeChild(el);
    showNotificationBanner(dispatch, "Copied!", NotificationType.AFFIRMATIVE);
  }, [dispatch, shareURL]);

  const shareProfileLink = useCallback(async () => {
    await onShareLink("Share your membership link!", shareURL);
  }, [shareURL]);

  const onMessagesClick = useCallback(() => {
    const url = `sms:&body=${encodeURIComponent(shareURL)}`;
    window.location.href = url;
  }, [shareURL]);

  const renderShareTo = useMemo(
    () => (
      <div
        className="ShareEventModalLinkIcon"
        style={theme ? theme.SecondaryBG : { backgroundColor: Colors.WHITE }}
      >
        <Icon icon="mdi:share-outline" height={26} />
      </div>
    ),
    [theme]
  );

  const downloadQRCode = useCallback(() => {
    if (!qrCodeRef.current) return;

    const svg = qrCodeRef.current.querySelector("svg");
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();

    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx?.drawImage(img, 0, 0);
      const pngFile = canvas.toDataURL("image/png");
      const downloadLink = document.createElement("a");
      downloadLink.download = `markit-membership-qr-code-${username}.png`;
      downloadLink.href = pngFile;
      downloadLink.click();
    };

    img.src = "data:image/svg+xml;base64," + btoa(svgData);
  }, [username]);

  return (
    <>
      <PopupModalContainer
        headerComp={
          <div className="ColumnNormal" style={{ gap: 10 }}>
            <Icon icon={"ion:share"} height={35} />
            <span className="sectionTitle">Share Membership Link</span>
          </div>
        }
        valueComp={
          <div className="ColumnNormal" style={{ gap: 14 }}>
            <AlertButtonWrapper
              buttonComp={
                <div
                  className="ColumnNormal"
                  style={{
                    backgroundColor: Colors.GRAY6,
                    padding: 14,
                    borderRadius: 12,
                    gap: 14,
                  }}
                >
                  <span className="bodyMedium">Membership Link</span>
                  <div
                    className="ShareEventModalLinks"
                    style={theme?.PrimaryText}
                  >
                    <div>
                      <div
                        className="ShareEventModalLinkIcon"
                        style={
                          theme
                            ? theme.SecondaryBG
                            : { backgroundColor: Colors.WHITE }
                        }
                        onClick={onCopyClick}
                      >
                        <Icon icon="ep:link" height={26} />
                      </div>
                      <div style={styles.smallText}>Copy Link</div>
                    </div>
                    {isDesktop ? (
                      <RWebShare data={{ url: shareURL }}>
                        <div className="ColumnNormal">
                          {renderShareTo}
                          <span style={styles.smallText}>Share to...</span>
                        </div>
                      </RWebShare>
                    ) : (
                      <div>
                        <div onClick={shareProfileLink}>{renderShareTo}</div>
                        <span style={styles.smallText}>Share to...</span>
                      </div>
                    )}
                    <div>
                      <div
                        className="ShareEventModalLinkIcon"
                        style={
                          theme
                            ? theme.SecondaryBG
                            : { backgroundColor: Colors.WHITE }
                        }
                        onClick={onMessagesClick}
                      >
                        <Icon icon="ion:chatbubble-outline" height={22} />
                      </div>
                      <div style={styles.smallText}>Messages</div>
                    </div>
                    <div>
                      <div
                        className="ShareEventModalLinkIcon"
                        style={
                          theme
                            ? theme.SecondaryBG
                            : { backgroundColor: Colors.WHITE }
                        }
                        onClick={() => setAlertText("Coming soon...")}
                      >
                        <Icon icon="mdi:instagram" height={24} />
                      </div>
                      <div style={styles.smallText}>Instagram</div>
                    </div>
                    <div>
                      <div
                        className="ShareEventModalLinkIcon"
                        style={
                          theme
                            ? theme.SecondaryBG
                            : { backgroundColor: Colors.WHITE }
                        }
                        onClick={() => setQrCodeModal(true)}
                      >
                        <Icon icon="ion:qr-code" height={22} />
                      </div>
                      <div style={{ ...styles.smallText, textAlign: "center" }}>
                        QR Code
                      </div>
                    </div>
                  </div>
                </div>
              }
              alertTextHeader={alertText}
              clearAlert={() => setAlertText("")}
            />
            <RectangleButton
              buttonLabel="Copy Link"
              iconRight={<Icon icon="ep:link" height={16} />}
              onPress={onCopyClick}
              containerStyles={{
                backgroundColor: Colors.BLACK,
                color: Colors.WHITE,
                paddingBlock: 16,
                paddingInline: 12,
              }}
            />
          </div>
        }
        theme={theme}
        noExit
        closeOnOutsidePress={!qrCodeModal}
        closeModal={() => setShareLinkModal(false)}
        modalContentWidth={PopupModalContainerWidth.SMALL}
      />
      {qrCodeModal ? (
        <PopupModalContainer
          headerComp={
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span className="AboutSubtitle">Share QR Code</span>
            </div>
          }
          valueComp={
            <div
              className="ColumnNormal"
              style={{
                gap: 20,
                paddingBlock: "30px",
                width: "256px",
                margin: "0 auto",
              }}
            >
              <div ref={qrCodeRef} className="Centering">
                <QRCode value={shareURL} />
              </div>
              <RectangleButton
                buttonLabel="Download QR Code"
                iconRight={<Icon icon="mdi:download" height={16} />}
                onPress={downloadQRCode}
                containerStyles={{
                  backgroundColor: Colors.BLACK,
                  color: Colors.WHITE,
                  paddingBlock: 16,
                  paddingInline: 12,
                  width: "100%",
                }}
              />
            </div>
          }
          noExit
          closeOnOutsidePress
          closeModal={() => setQrCodeModal(false)}
        />
      ) : null}
    </>
  );
};

export default ShareMembershipLinkModal;
